"use client";

import React from "react";
import { FileText, BookOpen, Video, Calculator, ArrowRight, Download, Sparkles, ExternalLink } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
const resourceFormats = [{
  id: "app-notes",
  iconName: "file-text",
  tag: "Practical Lab Guides",
  title: "Application Notes",
  description: "Method validation protocols, assay setups, and reproducible workflows tailored for QA/QC and formulation teams.",
  count: "48+ Documents",
  actionText: "Browse Notes",
  targetPath: "/ResourcesPage"
}, {
  id: "tech-guides",
  iconName: "book-open",
  tag: "In-Depth Science",
  title: "Technical Guides & Whitepapers",
  description: "Detailed regulatory compliance overviews, PAT integration frameworks, and analytical instrumentation benchmarks.",
  count: "24+ Publications",
  actionText: "Read Whitepapers",
  targetPath: "/ResourcesPage"
}, {
  id: "webinars",
  iconName: "video",
  tag: "On-Demand & Live",
  title: "Video Demos & Webinars",
  description: "Step-by-step equipment walkthroughs, live software telemetry demos, and expert-led methodology sessions.",
  count: "35+ Recordings",
  actionText: "Watch Demos",
  targetPath: "/BookADemoPage"
}, {
  id: "calculators",
  iconName: "calculator",
  tag: "Interactive Tools",
  title: "Interactive Calculators",
  description: "Solvent recovery ROI estimation, blend uniformity variance tools, and spectrometer cycle-time benchmarks.",
  count: "6 Smart Tools",
  actionText: "Launch Tools",
  targetPath: "/FeasibilityStudiesPage"
}];
export default function FormatsGrid() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case "file-text":
        return <FileText className="size-6 text-primary" />;
      case "book-open":
        return <BookOpen className="size-6 text-primary" />;
      case "video":
        return <Video className="size-6 text-primary" />;
      case "calculator":
        return <Calculator className="size-6 text-primary" />;
      default:
        return <FileText className="size-6 text-primary" />;
    }
  };
  return <section id="resourceformatsgrid" data-nav="light" className="w-full bg-background py-16 md:py-24 border-b border-border transition-colors">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-2xl mb-12 md:mb-16">
          <Badge variant="outline" className="mb-3.5 bg-secondary text-secondary-foreground border-border px-3 py-1 text-xs uppercase tracking-wider font-semibold">
            <Sparkles className="size-3.5 mr-1.5 text-primary" />
            Knowledge Base
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground font-default">
            Explore Available Resource Formats
          </h2>
          <p className="mt-3.5 text-base md:text-lg text-muted-foreground leading-relaxed">
            Access validated scientific literature, interactive engineering calculators, and application walkthroughs to optimize laboratory workflows.
          </p>
        </div>

        {/* Formats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {resourceFormats.map((format, i) => <motion.div key={format.id} data-index={i} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.45,
          delay: i * 0.1
        }} className="h-full">
              <Card className="h-full border border-border bg-card shadow-xs hover:shadow-md transition-[box-shadow,border-color] duration-300 hover:border-primary/40 flex flex-col justify-between group">
                <CardHeader className="gap-4 pb-2">
                  <div className="flex items-center justify-between">
                    <div className="size-12 rounded-lg bg-secondary flex items-center justify-center border border-border group-hover:bg-primary/10 transition-colors">
                      {getIcon(format.iconName)}
                    </div>
                    <span className="text-xs font-medium text-muted-foreground bg-muted px-2.5 py-1 rounded-md border border-border/60">
                      {format.count}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-primary uppercase tracking-wider">
                      {format.tag}
                    </span>
                    <CardTitle className="text-xl md:text-2xl font-semibold text-card-foreground mt-1.5 group-hover:text-primary transition-colors">
                      {format.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="pb-6">
                  <CardDescription className="text-sm md:text-base text-muted-foreground leading-relaxed">
                    {format.description}
                  </CardDescription>
                </CardContent>

                <CardFooter className="pt-0 border-t border-border/60 flex items-center justify-between">
                  <Button asChild variant="ghost" className="p-0 text-sm font-semibold text-foreground group-hover:text-primary hover:bg-transparent transition-colors justify-start">
                    <Link to={format.targetPath} className="inline-flex items-center gap-2">
                      <span>{format.actionText}</span>
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="sm" className="rounded-md border-border text-xs text-muted-foreground hover:text-foreground">
                    <Link to={format.targetPath} aria-label={`Open ${format.title}`}>
                      <ExternalLink className="size-3.5" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>

        {/* Footer Support Banner */}
        <div className="mt-12 p-6 md:p-8 rounded-xl bg-secondary/50 border border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold text-foreground font-default">
              Need a bespoke analytical method or feasibility assessment?
            </h3>
            <p className="text-sm text-muted-foreground">
              Our technical specialists assist with chemometrics, hardware validation, and application development.
            </p>
          </div>
          <Button asChild variant="default" className="shrink-0 shadow-xs">
            <Link to="/DiscussYourApplicationPage" className="inline-flex items-center gap-2">
              <span>Discuss Your Application</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}